import { useState, useEffect, useRef, FC } from "react";


const styles: string = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Jost:wght@300;400;500&display=swap');

  :root {
    --orange: rgb(255, 131, 0);
    --crimson: rgb(138, 36, 50);
    --dark: #222720;
    --dark-soft: #2c2f29;
    --orange-glow: rgba(255, 131, 0, 0.18);
    --crimson-glow: rgba(138, 36, 50, 0.25);
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    background: var(--dark);
    font-family: 'Jost', sans-serif;
    overflow-x: hidden;
    min-height: 100vh;
  }

  .cs-wrapper {
    min-height: 100vh;
    background: var(--dark);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    padding: 2rem;
  }

  .cs-wrapper::before {
    content: '';
    position: absolute;
    top: -10%;
    left: 50%;
    transform: translateX(-50%);
    width: 80vw;
    height: 60vh;
    background: radial-gradient(ellipse at center, rgba(138,36,50,0.22) 0%, rgba(255,131,0,0.08) 40%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  .cs-wrapper::after {
    content: '';
    position: absolute;
    bottom: -5%;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 40vh;
    background: radial-gradient(ellipse at center, rgba(255,131,0,0.06) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }

  #particleCanvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 1;
  }

  .deco-line {
    position: absolute;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,131,0,0.35), rgba(138,36,50,0.35), transparent);
    width: 70%;
    left: 15%;
    z-index: 2;
  }
  .deco-line.top { top: 12%; }
  .deco-line.bottom { bottom: 12%; }

  .side-accent {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 180px;
    background: linear-gradient(180deg, transparent, var(--orange), transparent);
    opacity: 0.4;
    z-index: 2;
    animation: pulse-line 3s ease-in-out infinite;
  }
  .side-accent.left { left: 4%; }
  .side-accent.right { right: 4%; }

  @keyframes pulse-line {
    0%, 100% { opacity: 0.25; height: 160px; }
    50% { opacity: 0.5; height: 210px; }
  }

  .cs-content {
    position: relative;
    z-index: 3;
    text-align: center;
    max-width: 820px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  .cs-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(138,36,50,0.2);
    border: 1px solid rgba(138,36,50,0.5);
    color: var(--orange);
    font-family: 'Jost', sans-serif;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 3px;
    text-transform: uppercase;
    padding: 7px 18px;
    border-radius: 30px;
    margin-bottom: 2rem;
    animation: fadeSlideDown 0.8s ease both;
  }
  .badge-dot {
    width: 6px; height: 6px;
    background: var(--orange);
    border-radius: 50%;
    animation: blink 1.8s ease infinite;
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }

  .cs-heading {
    font-family: 'Cinzel', serif;
    font-size: clamp(3.5rem, 9vw, 7.5rem);
    font-weight: 900;
    line-height: 0.92;
    letter-spacing: -1px;
    margin-bottom: 1.6rem;
    animation: fadeSlideUp 0.9s ease 0.2s both;
  }
  .cs-heading .line1 {
    display: block;
    color: #f5ede0;
  }
  .cs-heading .line2 {
    display: block;
    background: linear-gradient(135deg, var(--orange) 0%, #ff8500 40%, var(--crimson) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 28px rgba(255,131,0,0.4));
  }

  .cs-divider {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 1.4rem;
    animation: fadeSlideUp 0.9s ease 0.35s both;
    width: 260px;
  }
  .cs-divider span {
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,131,0,0.5));
  }
  .cs-divider span:last-child {
    background: linear-gradient(270deg, transparent, rgba(255,131,0,0.5));
  }
  .cs-divider-icon {
    width: 8px; height: 8px;
    background: var(--orange);
    transform: rotate(45deg);
    flex-shrink: 0;
  }

  .cs-subtitle {
    font-family: 'Jost', sans-serif;
    font-size: 14px;
    font-weight: 300;
    color: rgba(245,237,224,0.55);
    letter-spacing: 1px;
    line-height: 1.8;
    max-width: 460px;
    margin-bottom: 3rem;
    animation: fadeSlideUp 0.9s ease 0.45s both;
  }

  .cs-countdown {
    display: flex;
    gap: clamp(12px, 3vw, 28px);
    margin-bottom: 3.5rem;
    animation: fadeSlideUp 0.9s ease 0.55s both;
  }
  .cd-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 72px;
  }
  .cd-number {
    font-family: 'Cinzel', serif;
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 700;
    color: #f5ede0;
    line-height: 1;
    background: rgba(255,131,0,0.07);
    border: 1px solid rgba(255,131,0,0.2);
    border-radius: 10px;
    padding: clamp(12px, 2vw, 18px) clamp(14px, 2.5vw, 22px);
    min-width: clamp(64px, 10vw, 88px);
    text-align: center;
    position: relative;
    transition: border-color 0.3s ease;
  }
  .cd-number::after {
    content: '';
    position: absolute;
    bottom: 0; left: 10%; width: 80%; height: 1px;
    background: rgba(255,131,0,0.25);
  }
  .cd-number:hover { border-color: rgba(255,131,0,0.5); }
  .cd-label {
    font-size: 10px;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: rgba(255,131,0,0.65);
    font-weight: 500;
  }
  .cd-sep {
    font-family: 'Cinzel', serif;
    font-size: 2.4rem;
    color: rgba(255,131,0,0.35);
    align-self: flex-start;
    padding-top: clamp(12px, 2vw, 16px);
    line-height: 1;
  }

  .cs-form {
    display: flex;
    gap: 0;
    width: 100%;
    max-width: 460px;
    animation: fadeSlideUp 0.9s ease 0.65s both;
    margin-bottom: 2.5rem;
  }
  .cs-input {
    flex: 1;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,131,0,0.25);
    border-right: none;
    border-radius: 6px 0 0 6px;
    padding: 14px 20px;
    color: rgba(245,237,224,0.9);
    font-family: 'Jost', sans-serif;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s, background 0.3s;
  }
  .cs-input::placeholder { color: rgba(245,237,224,0.3); }
  .cs-input:focus {
    background: rgba(255,131,0,0.06);
    border-color: rgba(255,131,0,0.55);
  }
  .cs-btn {
    background: linear-gradient(135deg, var(--orange), var(--crimson));
    border: none;
    border-radius: 0 6px 6px 0;
    padding: 14px 26px;
    color: #fff;
    font-family: 'Jost', sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    cursor: pointer;
    transition: opacity 0.3s, transform 0.2s;
    white-space: nowrap;
  }
  .cs-btn:hover { opacity: 0.88; transform: translateX(1px); }
  .cs-btn:active { transform: scale(0.98); }

  .cs-success {
    color: var(--orange);
    font-size: 13px;
    letter-spacing: 1px;
    animation: fadeSlideUp 0.5s ease both;
    margin-bottom: 2.5rem;
  }

  .cs-socials {
    display: flex;
    gap: 14px;
    animation: fadeSlideUp 0.9s ease 0.75s both;
    margin-bottom: 3rem;
  }
  .cs-social-link {
    width: 38px; height: 38px;
    border: 1px solid rgba(255,131,0,0.2);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: rgba(245,237,224,0.45);
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.3s;
    cursor: pointer;
    background: rgba(255,131,0,0.03);
    font-family: 'Jost', sans-serif;
  }
  .cs-social-link:hover {
    border-color: var(--orange);
    color: var(--orange);
    background: rgba(255,131,0,0.08);
  }
  .brand-link{
    color: var(--orange);
    cursor: pointer;
  }
  .cs-footer {
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: rgba(245,237,224,0.2);
    animation: fadeSlideUp 0.9s ease 0.85s both;
  }
  .cs-footer span { color: rgba(255,131,0,0.45); }

  @keyframes fadeSlideDown {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .corner {
    position: absolute;
    width: 50px; height: 50px;
    z-index: 2;
    opacity: 0.4;
  }
  .corner.tl { top: 6%; left: 6%; border-top: 1px solid var(--orange); border-left: 1px solid var(--orange); }
  .corner.tr { top: 6%; right: 6%; border-top: 1px solid var(--orange); border-right: 1px solid var(--orange); }
  .corner.bl { bottom: 6%; left: 6%; border-bottom: 1px solid var(--orange); border-left: 1px solid var(--orange); }
  .corner.br { bottom: 6%; right: 6%; border-bottom: 1px solid var(--orange); border-right: 1px solid var(--orange); }

  @media (max-width: 520px) {
    .side-accent, .corner, .deco-line { display: none; }
    .cs-form { flex-direction: column; }
    .cs-input { border-right: 1px solid rgba(255,131,0,0.25); border-radius: 6px; }
    .cs-btn { border-radius: 6px; }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

type SocialHandle = "Tw" | "Ig" | "Fb" | "Li";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const pad = (n: number): string => String(n).padStart(2, "0");

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useCountdown(targetDate: Date): TimeLeft {
  const calc = (): TimeLeft => {
    const diff: number = targetDate.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86_400_000),
      hours: Math.floor((diff % 86_400_000) / 3_600_000),
      minutes: Math.floor((diff % 3_600_000) / 60_000),
      seconds: Math.floor((diff % 60_000) / 1_000),
    };
  };

  const [time, setTime] = useState<TimeLeft>(calc);

  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1_000);
    return () => clearInterval(id);
  }, []);

  return time;
}

// ─── Particle class (kept outside component to avoid re-creation) ─────────────

const EMBER_COLORS: string[] = [
  "rgba(255,131,0,",
  "rgba(255,100,0,",
  "rgba(138,36,50,",
  "rgba(255,180,60,",
];

class Particle {
  x: number = 0;
  y: number = 0;
  size: number = 0;
  speedY: number = 0;
  speedX: number = 0;
  alpha: number = 0;
  fade: number = 0;
  color: string = "";
  life: number = 1;

  constructor(private W: number, private H: number, init = false) {
    this.reset(init);
  }

  reset(init = false): void {
    this.x = Math.random() * this.W;
    this.y = init ? Math.random() * this.H : this.H + 10;
    this.size = Math.random() * 2.5 + 0.5;
    this.speedY = -(Math.random() * 0.8 + 0.3);
    this.speedX = (Math.random() - 0.5) * 0.4;
    this.alpha = Math.random() * 0.5 + 0.2;
    this.fade = Math.random() * 0.004 + 0.002;
    this.color = EMBER_COLORS[Math.floor(Math.random() * EMBER_COLORS.length)];
    this.life = 1;
  }

  update(): void {
    this.y += this.speedY;
    this.x += this.speedX;
    this.life -= this.fade;
    if (this.life <= 0 || this.y < -10) this.reset();
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = `${this.color}${this.alpha * this.life})`;
    ctx.fill();
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const ComingSoon: FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [email, setEmail] = useState<string>("");
  const [submitted, setSubmitted] = useState<boolean>(false);

  const target: Date = new Date(
    Date.now() + 47 * 86_400_000 + 6 * 3_600_000 + 14 * 60_000
  );
  const { days, hours, minutes, seconds } = useCountdown(target);

  // ── Particle canvas ──────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W: number = 0;
    let H: number = 0;
    let particles: Particle[] = [];
    let raf: number;

    const resize = (): void => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      // Rebuild particles on resize so coordinates stay valid
      particles = Array.from({ length: 90 }, () => new Particle(W, H, true));
    };

    resize();
    window.addEventListener("resize", resize);

    const loop = (): void => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.update();
        p.draw(ctx);
      });
      raf = requestAnimationFrame(loop);
    };

    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ── Form submit ──────────────────────────────────────────────────────────
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (email.trim()) {
      setSubmitted(true);
      setEmail("");
    }
  };

  const socials: SocialHandle[] = ["Tw", "Ig", "Fb", "Li"];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>

      <div className="cs-wrapper">
        <canvas id="particleCanvas" ref={canvasRef} />

        {/* Decorative elements */}
        <div className="deco-line top" />
        <div className="deco-line bottom" />
        <div className="side-accent left" />
        <div className="side-accent right" />
        <div className="corner tl" />
        <div className="corner tr" />
        <div className="corner bl" />
        <div className="corner br" />

        {/* Main content */}
        <div className="cs-content">

          {/* Badge */}
          <div className="cs-badge">
            <span className="badge-dot" />
            Something extraordinary is coming
          </div>

          {/* Heading */}
          <h1 className="cs-heading">
            <span className="line1">We Are</span>
            <span className="line2">Coming Soon</span>
          </h1>

          {/* Divider */}
          <div className="cs-divider">
            <span />
            <div className="cs-divider-icon" />
            <span />
          </div>

          {/* Subtitle */}
          {/* <p className="cs-subtitle">
            Something delicious is on its way. Our new online experience is being
            crafted with the same care as every crispy bite — stay close, the
            feast is almost ready.
          </p> */}

          {/* Countdown */}


          {/* Email form / success */}


          {/* Footer */}
          <p className="cs-footer">
            © 2026&nbsp;<a target="_blank" href="https://www.simplexsuite.com/" className="brand-link">Simplex Technology Solutions</a>&nbsp;— All rights reserved
          </p>

        </div>
      </div>
    </>
  );
};

export default ComingSoon;